// 会议室: walking into a zone the map marks as a meeting room (property meeting = true, deck/map.js) opens a Zoom-like
// view over the map: a gallery of everyone in the room (camera if on, else their otter on a coloured card; a green
// ring while they talk, 🔇, 🤖 for agents), a speaker view, a screen share taking the stage by itself, pinning, room
// chat and the participant list, and a toolbar: 🎤 📷 🖥 字幕 💬 👥 离开会议室. 邀请 is at the foot of the participant list
// (artin 11-03: 「会议邀请应该改到那个会议参与人里面」, as Zoom has it), no longer on the toolbar. The 「会议 | 地图」 switch at the top right
// (also on the small window) flips between this view and the office map with a small floating strip; voice stays on
// either way (it follows the room, not the view). The room chat opens on the right when I walk in, unless I closed it
// last time (remembered in localStorage MEET_CHAT_KEY; on a narrow screen it starts closed until I open it once).
// Walking out (or 离开会议室, which walks me out of the door) closes it and stops my camera.
// 字幕 (live captions, public/stt.js + office/stt.ts): a strip at the bottom of the view with what the last few
// speakers are saying, word by word as they speak (partials, lighter) and then the finished sentence in its place;
// each line fades CAP_FADE_MS after its last word. Whoever is being transcribed has the 转写中 icon (a speech bubble
// with a yellow mic, as on their name tag) on their tile and in the participant list. The toolbar's 字幕 is
// 设置 → 会议室字幕 (the office sends live words only to who has it on).
// Voice needs nothing new here: everyone in one room hears everyone at full volume (office/voice.ts, app.js volumes).
// The camera is the one new track: published through the same SFU session as the mic (640×360, 15 fps), announced
// with {t:"cam", trackName} (office/voice.ts takes it only inside a 会议室, and drops it when I walk out), and pulled
// by app.js only by people in the same room (syncAudio).
// 通话 (public/call.js) uses this same view: while I'm in a call it shows the call instead of the room (its people
// wherever they are, other floors too), without the room's chat and 白板; the list's 邀请 is 添加 (ring more people), 离开会议室 is 挂断,
// and the small window (地图) has 挂断 too. The camera and screen share work in a call as in a room.
// 主持人 (public/meethost.js, office/meethost.ts): the host has a crown and 「主持人」 in the list and a crown on their
// tile; the list's foot has 邀请, 静音我 and 认领主持人, or for the host 全员静音 and 锁门 / 开锁; the host's ⋯ on a row
// opens 静音 / 请离开 for that person. Not in a call.
const CAM_CONSTRAINTS = { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 15, max: 15 }, facingMode: 'user' };
const CAM_MAX_BITRATE = 600_000;
const COLORS = ['#e76f51', '#2a9d8f', '#e9c46a', '#8e7dbe', '#f4a261', '#4f9dde', '#d16ba5', '#6a994e'];
const SPEAK_HOLD_MS = 1500; // the speaker view keeps the last speaker this long after they stop
const MINI_TILES = 4;
const CAM_PULL_MAX = 6; // cameras pulled at once: the most recent speakers (plus a pinned tile); the rest show their otter
const CHAT_KEEP = 200; // lines in the meeting's chat (office/chat.ts MEET_KEEP keeps as many)
const MEET_CHAT_KEY = 'deck.meetChat'; // '1': the room chat opens when I walk in, '0': it stays closed
const CAP_LINES = 3, CAP_FADE_MS = 7000, CAP_TAIL = 120; // captions: speakers shown, fade after, code points kept (the newest)

import { icon, put, hasIcon } from '/icons.js';
import { t, tr, onLang, fmtTime } from '/i18n.js';
/** The toolbar's state-dependent words (catalog keys, public/i18n.js), as data: render() only says which state. */
const WORDS = {
  mic: { on: 'meeting.bar.mic.on', muted: 'meeting.bar.mic.muted', none: 'meeting.bar.mic.none' },
  cam: { off: 'meeting.bar.cam.off', on: 'meeting.bar.cam.on' },
  share: { off: 'meeting.bar.share.off', live: 'meeting.bar.share.live' },
  cc: { on: 'meeting.bar.cc.on', off: 'meeting.bar.cc.off' },
  rec: { off: { text: 'meeting.bar.rec.off', tip: 'meeting.bar.rec.offTip' }, on: { text: 'meeting.bar.rec.on', tip: 'meeting.bar.rec.onTip' } },
  invite: { room: { icon: 'door', text: 'meeting.bar.invite.room', tip: 'meeting.bar.invite.roomTip' }, call: { icon: 'phone', text: 'meeting.bar.invite.call', tip: 'meeting.bar.invite.callTip' } },
  leave: { room: { icon: null, text: 'meeting.bar.leave' }, call: { icon: 'hangup', text: 'meeting.bar.hangup' } },
};
/** 「主持人」 / Host (the crown's title on a tile). */
const hostWord = () => t('meetHost.host');
/** A name, with （你） / (you) when it's mine. */
const nameOf = (name, mine) => (mine ? t('meeting.youName', { name }) : name);
/** A chat or caption line's speaker: 「X：」 */
const saidBy = (name) => t('meeting.said', { name: name || t('meeting.otter') });
const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const hash = (s) => { let h = 0; for (const c of String(s)) h = (h * 31 + c.codePointAt(0)) | 0; return Math.abs(h); };

const CSS = `
#meet{position:fixed;inset:0;z-index:40;display:none;flex-direction:column;background:#1c1f26;color:#f4efe4;font:14px/1.4 var(--ui-font,-apple-system,system-ui,sans-serif);
  padding:calc(8px + var(--st,0px)) calc(8px + var(--sr,0px)) calc(8px + var(--sb,0px)) calc(8px + var(--sl,0px));box-sizing:border-box}
body.meet-full #meet{display:flex} body.meet-full #screens{display:none}
body:not(.meet-in) .bar #share{display:none}
.toast{z-index:60}
#meet .mhead{display:flex;align-items:center;gap:8px;padding:2px 4px 8px;min-height:32px}
#meet .mtitle{font-weight:700;font-size:15px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
#meet .mcount{color:#b9b2a5;font-size:12px;white-space:nowrap}
#meet .mhead .sp{flex:1}
#meet .mseg{display:flex;border:2px solid #3a3f4b;border-radius:999px;overflow:hidden}
#meet .mseg button,#meet .mhead>button{font:inherit;font-size:12px;border:0;background:transparent;color:#f4efe4;padding:4px 10px;cursor:pointer;white-space:nowrap}
#meet .mseg button.on{background:#FDD03B;color:#1b1410}
#meet .mhead>button{border:2px solid #3a3f4b;border-radius:999px}
.mvseg{display:flex;border:2px solid #3a3f4b;border-radius:999px;overflow:hidden;flex:none}
.mvseg button{font:inherit;font-size:12px;border:0;background:transparent;color:#f4efe4;padding:4px 10px;cursor:pointer;white-space:nowrap;display:inline-flex;align-items:center;gap:4px}
.mvseg button.on{background:#FDD03B;color:#1b1410;cursor:default}
#meetMini .mvseg button{padding:2px 8px}
#meet .mmain{flex:1;display:flex;gap:8px;min-height:0}
#meet .mview{flex:1;display:flex;flex-direction:column;gap:8px;min-width:0;min-height:0;position:relative}
#meet .mcap{position:absolute;left:50%;bottom:10px;transform:translateX(-50%);width:min(760px,calc(100% - 24px));display:flex;flex-direction:column;gap:3px;z-index:3;pointer-events:none}
#meet .mcap .cl{align-self:center;max-width:100%;background:rgba(12,13,16,.82);color:#fff;border-radius:8px;padding:3px 10px;font-size:16px;line-height:1.45;word-break:break-word;transition:opacity .6s}
#meet .mcap .cl .n{color:#FDD03B;font-weight:700;margin-right:4px} #meet .mcap .cl.mine .n{color:#c4b5fd}
#meet .mcap .cl.partial .x{color:#c9c4ba} #meet .mcap .cl.gone{opacity:0}
.tile .stt{position:absolute;left:6px;top:6px;background:rgba(20,20,24,.72);border-radius:6px;padding:3px 5px;line-height:0}
#meetMini .tile .stt{display:none}
#meet .mbar button .i.txt{font-size:12px;font-weight:800;line-height:1;height:auto;border:2px solid currentColor;border-radius:4px;padding:2px 3px;margin:3px 0 2px}
#meet .mstrip{display:none;gap:8px;overflow-x:auto;flex:none;padding-bottom:2px}
#meet .mstrip .tile{width:160px;height:90px;flex:none}
#meet .mstage{display:none;flex:1;min-height:0;position:relative;align-items:center;justify-content:center}
#meet.staged .mstrip,#meet.staged .mstage{display:flex}
#meet.staged .mgrid{display:none}
#meet .mstage .tile{width:100%;height:100%}
#meet .mgrid{flex:1;display:flex;flex-wrap:wrap;align-content:center;justify-content:center;gap:8px;min-height:0;overflow:hidden}
#meet .tile{position:relative;border-radius:12px;overflow:hidden;background:var(--c);background-clip:padding-box;box-sizing:border-box;border:3px solid transparent;transition:border-color .15s}
#meet .tile.speaking,#meetMini .tile.speaking{border-color:#22c55e;box-shadow:0 0 0 2px rgba(34,197,94,.35)}
.tile video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;background:#000;display:none}
.tile.cam video{display:block} .tile.mine video{transform:scaleX(-1)}
.tile.cam .av{display:none}
.tile .av{position:absolute;left:50%;top:46%;transform:translate(-50%,-50%);height:58%;max-height:128px;aspect-ratio:1;image-rendering:pixelated}
.tile .nm{position:absolute;left:6px;bottom:6px;max-width:calc(100% - 12px);background:rgba(20,20,24,.72);color:#fff;border-radius:6px;padding:1px 7px;font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tile .pin{position:absolute;right:6px;top:6px;border:0;border-radius:999px;background:rgba(20,20,24,.6);color:#fff;font-size:12px;padding:2px 7px;cursor:pointer;opacity:0;transition:opacity .15s}
.tile:hover .pin,.tile.pinned .pin{opacity:1} @media (hover:none){.tile .pin{opacity:.85}}
.tile.pinned .pin{background:#FDD03B;color:#1b1410}
.tile .mut{position:absolute;right:6px;bottom:6px;background:#ef4444;color:#fff;border-radius:999px;font-size:11px;padding:1px 5px}
#meet .screen{position:relative;width:100%;height:100%;background:#000;border-radius:12px;overflow:hidden}
#meet .screen video{width:100%;height:100%;object-fit:contain;display:block}
#meet .screen .who{position:absolute;left:8px;top:8px;background:#FDD03B;color:#1b1410;border-radius:999px;padding:0 10px;font-size:12px;font-weight:600}
#meet .mside{display:none;width:300px;flex:none;flex-direction:column;background:#262a33;border-radius:12px;overflow:hidden}
#meet.side .mside{display:flex}
#meet .mside .sh{display:flex;align-items:center;padding:8px 10px;font-weight:700;border-bottom:1px solid #3a3f4b}
#meet .mside .sh button{margin-left:auto;border:0;background:transparent;color:#f4efe4;font-size:16px;cursor:pointer}
#meet .mlist{flex:1;overflow:auto;padding:6px 10px;font-size:13px}
#meet .mlist .line{margin:3px 0;word-break:break-word} #meet .mlist .n{font-weight:700;margin-right:4px;color:#FDD03B} #meet .mlist .mine .n{color:#c4b5fd}
#meet .mlist .t{color:#8b8579;font-size:11px;margin-right:4px} #meet .mlist .empty{color:#8b8579}
#meet .mlist .row{display:flex;align-items:center;gap:8px;padding:4px 0} #meet .mlist .row .nn{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#meet .mlist .row .ic{white-space:nowrap;letter-spacing:2px}
#meet .mfoot{display:flex;justify-content:center;padding:10px;border-top:1px solid #3a3f4b}
#meet .mfoot .minv{font:inherit;font-size:13px;font-weight:700;display:inline-flex;align-items:center;gap:6px;min-width:140px;justify-content:center;padding:6px 16px;border:2px solid #1b1410;border-radius:10px;background:#FDD03B;color:#1b1410;cursor:pointer}
#meet .mfoot .minv:hover{background:#ffe066}
#meet .mfoot{flex-wrap:wrap;gap:6px}
#meet .mfoot .mhb{font:inherit;font-size:12px;display:inline-flex;align-items:center;gap:5px;padding:5px 10px;border:2px solid #4a505e;border-radius:10px;background:#323743;color:#f4efe4;cursor:pointer;white-space:nowrap}
#meet .mfoot .mhb:hover{background:#3b4150} #meet .mfoot .mhb:disabled{opacity:.5;cursor:default}
#meet .mfoot .mhb.on{border-color:#FDD03B;color:#FDD03B}
#meet .mfoot .mhnote{flex-basis:100%;text-align:center;font-size:11px;color:#b9b2a5}
#meet .mlist .row .hb{display:inline-flex;align-items:center;gap:3px;flex:none;background:#FDD03B;color:#1b1410;border-radius:999px;padding:0 7px 0 3px;font-size:11px;font-weight:700}
#meet .mlist .row .mm{border:0;background:transparent;color:#f4efe4;cursor:pointer;padding:2px 4px;border-radius:6px;line-height:0} #meet .mlist .row .mm:hover,#meet .mlist .row .mm.on{background:#3a3f4b}
#meet .mlist .rmenu{display:flex;gap:6px;justify-content:flex-end;padding:0 0 6px}
#meet .mlist .rmenu button{font:inherit;font-size:12px;display:inline-flex;align-items:center;gap:4px;border:2px solid #4a505e;border-radius:8px;background:#323743;color:#f4efe4;padding:3px 8px;cursor:pointer}
#meet .mlist .rmenu button.bad{border-color:#dc2626;color:#fca5a5}
.tile .nm .px-icon[data-icon=crown]{margin-right:1px}
#meet .mchatin{display:flex;gap:6px;padding:8px;border-top:1px solid #3a3f4b}
#meet .mchatin input{flex:1;min-width:0;font:inherit;padding:6px 8px;border:0;border-radius:8px;background:#fff8e7;color:#1b1410}
#meet .mbar{display:flex;justify-content:center;align-items:center;gap:6px;padding-top:8px;flex-wrap:nowrap}
#meet .mbar button{position:relative;font:inherit;font-size:12px;display:flex;flex-direction:column;align-items:center;gap:1px;min-width:64px;padding:5px 8px;border:0;border-radius:10px;background:transparent;color:#f4efe4;cursor:pointer}
#meet .mbar button:hover{background:#2d323c} #meet .mbar button .i{font-size:20px;line-height:0;height:32px}
#meet .mbar button.off .i{filter:grayscale(1);opacity:.75} #meet .mbar button.off{color:#fca5a5}
#meet .mbar button.live{background:#14532d}
#meet .mbar .leave{background:#dc2626;color:#fff;margin-left:10px;padding:8px 14px;font-weight:700;flex-direction:row;font-size:13px}
#meet .mbar .leave:hover{background:#b91c1c}
#meet .mbar .badge{position:absolute;top:0;right:8px;background:#ef4444;color:#fff;border-radius:999px;font-size:10px;padding:0 5px;display:none}
#meetMini{position:fixed;left:calc(12px + var(--sl,0px));top:calc(52px + var(--st,0px));z-index:30;display:none;flex-direction:column;gap:6px;background:#1c1f26;color:#f4efe4;border:3px solid #1b1410;border-radius:14px;padding:6px;font:12px/1.4 var(--ui-font,-apple-system,system-ui,sans-serif);width:200px}
body.meet-mini #meetMini{display:flex}
#meetMini .mh{display:flex;align-items:center;gap:6px} #meetMini .mh b{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
#meetMini .mend{display:none;border:0;border-radius:999px;background:#dc2626;padding:3px 8px;cursor:pointer;line-height:0} #meetMini.call .mend{display:block}
#meet .mrung{color:#FDD03B;font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0}
#meet.call .mbar .bd,#meet.call .mbar .ch,#meet.call .mbar .cc,#meet.call .mbar .nt{display:none!important}
body.meet-call #secbar{z-index:41} /* talking to an agent (secretary.js): its hold-to-talk and end buttons stay usable over the call view */
#meetMini .ms{display:grid;grid-template-columns:1fr 1fr;gap:4px}
#meetMini .tile{position:relative;aspect-ratio:16/9;border-radius:8px;overflow:hidden;background:var(--c);background-clip:padding-box;border:2px solid transparent}
#meetMini .tile .nm{font-size:10px;left:3px;bottom:3px;padding:0 4px} #meetMini .tile .pin,#meetMini .tile .mut{display:none}
@media (max-width:640px){
  #meet .mbar{gap:0;justify-content:space-between} #meet .mbar button{min-width:0;padding:4px 6px;font-size:10px} #meet .mbar button .i{font-size:18px}
  #meet .mbar button .i.txt{font-size:11px} #meet .mcap{bottom:6px} #meet .mcap .cl{font-size:14px}
  #meet .mbar .leave{margin-left:4px;padding:8px 10px;font-size:12px}
  #meet .mstrip .tile{width:112px;height:63px}
  #meet .mside{position:absolute;left:8px;right:8px;bottom:calc(72px + var(--sb,0px));top:calc(56px + var(--st,0px));width:auto;z-index:2;box-shadow:0 8px 30px rgba(0,0,0,.5)}
  #meet .mhead .lbl{display:none} .mvseg button{font-size:0;gap:0;padding:4px 9px} /* icons only (the titles say which) */
  #meetMini{width:156px}
}`;

/**
 * @param {object} o
 * @param {() => object|null} o.me  @param {() => Map} o.peers  @param {(x,y) => object|null} o.zoneAt the zone object (collide.js)
 * @param {object} o.mover deck/move.js (footOk, travelTo, keys, clear)
 * @param {(p) => boolean} o.speaking  @param {(p) => object} o.lookOf  @param o.buildSheet  @param o.otterImgs  @param {(p, name?) => string} o.agentName
 * @param {(m) => void} o.send  @param {(text) => void} o.toast
 * @param {(slot, room) => void} [o.raftChannel] fills the header's Raft 频道 slot for the room (public/raft-links.js)
 * @param {{muted: () => boolean, ready: () => boolean, toggle: () => void}} o.mic
 * @param {{live: () => boolean, stream: () => MediaStream|null, toggle: () => void, stop: () => void}} o.share my screen share (app.js)
 * @param {object} o.rtc the selected transport (app.js)
 * @param {object} [o.host] 主持人 (public/meethost.js): state(room), mine(), canClaim(), mute(id), muteAll(), remove(id), lock(on), claim()
 * @param {{open: () => void}} [o.invite] 邀请: the picker of people online to invite in (public/meetinvite.js)
 * @param {{view: () => ({id, list: {id, p}[], rung: string[]} | null), hangup: () => void, add: (() => void) | null}} [o.call] my 通话
 *   (public/call.js): its people other than me (on this floor: their peer; elsewhere: a peer-shaped stand-in)
 * @param {{supported: () => boolean, canStart: () => boolean, live: (zone) => object|null, toggle: () => void, open: () => void, entered: (zone) => void}} [o.notes]
 *   会议纪要 (public/notes.js): 记录 / 停止记录, 纪要, and the header's 正在记录
 * @param {{on: () => boolean, toggle: () => void, me: () => boolean, tip: () => string, tipOther: string}} o.cc 会议室字幕
 *   (stt.js ccMeet), whether I'm being transcribed now (my 转写中; the others' is p.stt, from the office's stt_on), and
 *   the 转写中 icon's tooltip (mine says why)
 */
export function initMeeting(o) {
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  let room = null;          // the meeting zone I'm in, or my call ({id: 'call:<id>', cid, name, call: true}), or null
  let shareEpoch = 0;       // a new authorization each time a room or call is entered, even the same one again
  // the room chat of the meeting I'm in (or was in last): what the office sent when I walked in ({t:"chat_log"},
  // office/chat.ts: kept for as long as the meeting goes on) and every line since, so walking out and back in (or a
  // reload) shows the meeting's chat so far, not an empty panel
  let kept = { room: null, lines: [] };
  let mode = 'full';        // 'full' | 'mini'
  let layout = 'gallery';   // 'gallery' | 'speaker'
  let pinned = null;        // a participant id ('me' for me), or null
  let side = null;          // 'chat' | 'people' | null
  let unread = 0, leaving = false;
  let active = null, activeAt = 0; // the active speaker (id) and when they last spoke
  const cam = { on: false, busy: false, mid: null, stream: null, name: null };
  const tiles = new Map();  // id -> {el, video, av, nm, mut, pin, lookKey, stream}
  const spokeAt = new Map(); // id -> when they last talked (which cameras to pull: camAllowed)
  const caps = new Map();   // id -> {el, nm, x, at}: a caption line per speaker ('me' for me), oldest first

  // ---- the DOM
  const root = el('div'); root.id = 'meet';
  const head = el('div', 'mhead'), title = el('span', 'mtitle'), count = el('span', 'mcount');
  const seg = el('div', 'mseg'), bGal = el('button'), bSpk = el('button');
  bGal.onclick = () => { layout = 'gallery'; pinned = null; render(); }; bSpk.onclick = () => { layout = 'speaker'; render(); };
  seg.append(bGal, bSpk);
  // 会议 | 地图: the meeting view or the office map (the meeting shrinks to a small window); voice is the same in both
  const viewSeg = () => {
    const w = el('div', 'mvseg'), a = el('button'), b = el('button');
    a.onclick = () => { if (mode !== 'full' || leaving) { leaving = false; setMode('full'); } };
    b.onclick = () => { if (mode !== 'mini') setMode('mini'); };
    w.append(a, b); w._a = a; w._b = b; return w;
  };
  const vsHead = viewSeg(), vsMini = viewSeg();
  const rch = el('span', 'mrch'); // the room's Raft 频道 (public/raft-links.js)
  const rungEl = el('span', 'mrung');
  const recTag = el('span', 'mrec'), recLabel = el('span'); recTag.append(el('i'), recLabel); // 会议纪要 (public/notes.js)
  head.append(title, recTag, count, rungEl, rch, el('span', 'sp'), seg, vsHead);
  const main = el('div', 'mmain'), view = el('div', 'mview');
  const strip = el('div', 'mstrip'), stage = el('div', 'mstage'), grid = el('div', 'mgrid'), capBox = el('div', 'mcap');
  capBox.setAttribute('aria-live', 'polite');
  view.append(strip, stage, grid, capBox);
  const sideBox = el('div', 'mside'), sideHead = el('div', 'sh'), sideTitle = el('span'), sideX = el('button'); sideX.append(icon('close'));
  tr(sideX, null, null, { title: 'meeting.close' }); sideX.onclick = () => setSide(null, true); sideHead.append(sideTitle, sideX);
  const chatList = el('div', 'mlist'), peopleList = el('div', 'mlist');
  chatList.append(tr(el('div', 'empty'), 'meeting.chat.empty'));
  const chatForm = el('div', 'mchatin'), chatIn = el('input'); chatIn.maxLength = 200; tr(chatIn, null, null, { placeholder: 'meeting.chat.placeholder' }); chatIn.autocomplete = 'off';
  const chatGo = tr(el('button'), 'meeting.chat.send'); chatGo.style.cssText = 'font:inherit;border:0;border-radius:8px;background:#FDD03B;color:#1b1410;padding:0 12px;cursor:pointer';
  chatForm.append(chatIn, chatGo);
  // 邀请 (a call: 添加) at the foot of the participant list, as Zoom has it (artin 11-03); shown only with the list
  const peopleFoot = el('div', 'mfoot'), bInvite = el('button', 'minv');
  bInvite.onclick = () => (room?.call ? o.call.add?.() : o.invite?.open()); peopleFoot.append(bInvite);
  // 主持人 (public/meethost.js): 静音我 and 认领主持人; the host: 全员静音 and 锁门 / 开锁
  const hbtn = (fn) => { const b = el('button', 'mhb'); b.onclick = fn; peopleFoot.append(b); return b; };
  const bMuteMe = hbtn(() => o.mic.toggle());
  const bClaim = hbtn(() => o.host?.claim());
  const bMuteAll = hbtn(() => o.host?.muteAll());
  const bLock = hbtn(() => { const s = room && o.host?.state(room.id); o.host?.lock(!s?.locked); });
  const hostNote = el('div', 'mhnote'); peopleFoot.append(hostNote);
  let menuFor = null, peopleSig = ''; // the row whose 静音 / 请离开 is open; what the list shows now (rebuilt only when it changes)
  sideBox.append(sideHead, chatList, peopleList, peopleFoot, chatForm);
  main.append(view, sideBox);
  const bar = el('div', 'mbar');
  // ic: a pixel icon's name (2x), or a short text (CC)
  const btn = (ic, label, fn) => { const b = el('button'); const i = el('span', 'i'), l = el('span', 'l', label); if (hasIcon(ic)) i.append(icon(ic, { size: 32 })); else i.textContent = ic; b.append(i, l); b.onclick = fn; b._i = i; b._l = l; bar.append(b); return b; };
  const bMic = btn('mic', '', () => o.mic.toggle());
  const bCam = btn('cam', '', () => toggleCam());
  // 参会者 and 聊天 right after the camera, as Zoom has them: on a phone the bar scrolls sideways under 离开会议室, and
  // 邀请 is at the foot of the 参会者 list, so both have to be on screen without a swipe (360 px, English labels included)
  const bPeople = btn('people', '', () => setSide(side === 'people' ? null : 'people'));
  const bChat = btn('chat', '', () => setSide(side === 'chat' ? null : 'chat', true));
  const chatBadge = el('span', 'badge'); bChat.append(chatBadge);
  const bShare = btn('screen', '', () => o.share.toggle());
  const bBoard = btn('board', '', () => dispatchEvent(new CustomEvent('deck-board', { detail: { room: room?.id } }))); // 白板 (public/whiteboard.js)
  const bCc = btn('CC', '', () => { o.cc.toggle(); if (!o.cc.on()) capClear(); render(); });
  bCc._i.classList.add('txt'); bCc.classList.add('cc');
  // 会议纪要: 记录 starts / stops recording this room (anyone here may stop it); 纪要 lists the notes I took part in
  // (artin 11-02: 记录 / 纪要 alone didn't say what they do) the hint shows on hover, and on a phone on a long press
  const bRec = o.notes ? btn('note', '', () => o.notes.toggle()) : null;
  const bNotes = o.notes ? btn('pencil', '', () => o.notes.open()) : null;
  /** A long press on a phone shows the button's hint (its title) instead of pressing it. */
  const holdTip = (b) => {
    let timer = 0, shown = false;
    b.addEventListener('touchstart', () => { shown = false; clearTimeout(timer); timer = setTimeout(() => { shown = true; o.toast(b.title); }, 500); }, { passive: true });
    for (const ev of ['touchend', 'touchmove', 'touchcancel']) b.addEventListener(ev, () => clearTimeout(timer), { passive: true });
    b.addEventListener('click', (e) => { if (shown) { shown = false; e.stopImmediatePropagation(); e.preventDefault(); } }, { capture: true });
    b.addEventListener('contextmenu', (e) => e.preventDefault());
  };
  if (bRec) holdTip(bRec);
  if (bNotes) holdTip(bNotes);
  bRec?.classList.add('nt'); bNotes?.classList.add('nt'); // (not in a call: notes are a 会议室's)
  const bLeave = el('button', 'leave'); bLeave.onclick = () => (room?.call ? o.call.hangup() : leave()); bar.append(bLeave);
  root.append(head, main, bar);
  const mini = el('div'); mini.id = 'meetMini';
  const miniHead = el('div', 'mh'), miniTitle = el('b'), miniEnd = el('button', 'mend'); miniEnd.append(icon('hangup')); tr(miniEnd, null, null, { title: 'meeting.bar.hangup' });
  miniEnd.onclick = () => o.call?.hangup(); miniHead.append(miniTitle, vsMini, miniEnd);
  const miniStrip = el('div', 'ms'); mini.append(miniHead, miniStrip);
  /** The words that don't change with the state (and the room-or-call ones), again on every language change. */
  function words() {
    put(bGal, 'grid', t('meeting.layout.gallery')); put(bSpk, 'speaker_view', t('meeting.layout.speaker'));
    for (const v of [vsHead, vsMini]) { put(v._a, 'cam', t('meeting.view.meet')); put(v._b, 'pin', t('meeting.view.map')); v._a.title = t('meeting.view.meetTip'); v._b.title = t('meeting.view.mapTip'); }
    bBoard._l.textContent = t('meeting.bar.board'); bCc.title = t('meeting.bar.cc.tip'); bChat._l.textContent = t('meeting.bar.chat');
    if (bNotes) { bNotes._l.textContent = t('meeting.bar.notes'); bNotes.title = t('meeting.bar.notesTip'); }
    const w = WORDS.invite[room?.call ? 'call' : 'room'], lw = WORDS.leave[room?.call ? 'call' : 'room'];
    put(bInvite, w.icon, t(w.text)); bInvite.title = t(w.tip);
    put(bLeave, lw.icon, t(lw.text));
  }
  words();
  onLang(() => { if (room?.call) room.name = t('meeting.call'); words(); if (room) setSide(side); }); // setSide: its title, then render()
  document.body.append(root, mini);
  // nothing typed into the meeting moves the otter or fires a map shortcut
  for (const t of ['keydown', 'keyup']) root.addEventListener(t, (e) => { if (e.target === chatIn) e.stopPropagation(); });
  chatIn.addEventListener('keydown', (e) => {
    if (e.isComposing || e.keyCode === 229) return;
    if (e.key === 'Enter') { e.preventDefault(); sendChat(); } else if (e.key === 'Escape') { chatIn.blur(); }
  });
  chatGo.onclick = sendChat;
  function sendChat() { const text = chatIn.value.trim(); if (text) o.send({ t: 'chat', text }); chatIn.value = ''; chatIn.focus(); }

  // ---- who is in the room
  const meNow = () => o.me();
  function people() {
    const me = meNow(); if (!me || !room) return [];
    const out = [{ id: 'me', p: me, mine: true }];
    if (room.call) { for (const x of o.call?.view()?.list ?? []) out.push({ ...x, mine: false }); return out; }
    for (const [id, p] of o.peers()) if (o.zoneAt(p.x, p.y)?.id === room.id) out.push({ id, p, mine: false });
    return out;
  }
  const camStreamOf = (x) => (x.mine ? cam.stream : x.p.camStream || null);
  const screenOf = (list) => {
    const mine = o.share.live() && o.share.stream();
    if (mine) return { id: 'me', name: t('meeting.you'), stream: mine };
    for (const x of list) if (!x.mine && x.p.screenStream && x.p.screenTrack) return { id: x.id, name: x.p.name, stream: x.p.screenStream };
    return null;
  };

  // ---- tiles: one element per participant, moved between the grid, the stage, the strip and the mini window
  function tileFor(x) {
    let t = tiles.get(x.id);
    if (!t) {
      const e = el('div', 'tile'), video = el('video'), av = el('canvas', 'av'), nm = el('div', 'nm'), mut = el('div', 'mut'), pin = el('button', 'pin'); mut.append(icon('mic_off')); pin.append(icon('thumbtack'));
      const stt = el('div', 'stt'); stt.append(icon('transcribing'));
      video.autoplay = true; video.playsInline = true; video.muted = true; av.width = av.height = 64;
      tr(pin, null, null, { title: 'meeting.pin' }); pin.onclick = (ev) => { ev.stopPropagation(); pinned = pinned === x.id ? null : x.id; render(); };
      e.append(av, video, nm, mut, stt, pin);
      t = { el: e, video, av, nm, mut, stt, pin, lookKey: null, stream: null };
      tiles.set(x.id, t);
    }
    const p = x.p;
    t.el.style.setProperty('--c', COLORS[hash(p.seed || p.sub || p.id) % COLORS.length]);
    t.el.classList.toggle('mine', x.mine);
    t.el.classList.toggle('pinned', pinned === x.id);
    put(t.nm, isHostX(x) ? 'crown' : p.kind === 'agent' ? 'robot' : null, nameOf(o.agentName(p), x.mine));
    t.nm.title = isHostX(x) ? hostWord() : '';
    t.mut.style.display = (x.mine ? o.mic.muted() : p.muted) ? '' : 'none';
    t.stt.style.display = transcribed(x) ? '' : 'none'; t.stt.title = sttTip(x);
    const look = o.lookOf(p), key = JSON.stringify(look);
    if (key !== t.lookKey) { // their otter, facing front, from the same sheet the map draws
      t.lookKey = key; const g = t.av.getContext('2d'); g.imageSmoothingEnabled = false; g.clearRect(0, 0, 64, 64);
      try { g.drawImage(o.buildSheet(o.otterImgs, look), 0, 0, 32, 32, 0, 0, 64, 64); } catch {}
    }
    const s = camStreamOf(x);
    if (s !== t.stream) { t.stream = s; t.video.srcObject = s; if (s) t.video.play().catch(() => {}); }
    t.el.classList.toggle('cam', !!s);
    return t;
  }
  let screenBox = null;
  function screenEl(sc) {
    if (!screenBox) { screenBox = el('div', 'screen'); const v = el('video'); v.autoplay = true; v.playsInline = true; v.muted = true; screenBox.append(v, el('div', 'who')); }
    const v = screenBox.firstChild;
    if (v.srcObject !== sc.stream) { v.srcObject = sc.stream; v.play().catch(() => {}); }
    screenBox.lastChild.textContent = sc.id === 'me' ? t('meeting.screen.mine') : t('meeting.screen.other', { name: sc.name });
    return screenBox;
  }
  /** The gallery: the column count and tile shape (16:9 on a wide box, down to 3:4 on a phone held upright) that
   *  give the biggest tiles. Cameras fill their tile (object-fit: cover). */
  function fitGrid(n) {
    const W = grid.clientWidth, H = grid.clientHeight, gap = 8;
    if (!W || !H || !n) return;
    let best = { w: 0, h: 0 };
    for (const a of [16 / 9, 4 / 3, 1, 3 / 4]) for (let cols = 1; cols <= n; cols++) {
      const rows = Math.ceil(n / cols);
      let w = (W - gap * (cols - 1)) / cols, h = w / a;
      if (h * rows + gap * (rows - 1) > H) { h = (H - gap * (rows - 1)) / rows; w = h * a; }
      if (w * h > best.w * best.h * 1.15 || (!best.w && w > 0)) best = { w, h }; // a wider shape wins unless clearly smaller
    }
    for (const c of grid.children) { c.style.width = Math.floor(best.w) + 'px'; c.style.height = Math.floor(best.h) + 'px'; }
  }
  /** Put these elements in this box, in this order, moving only the ones not already in place. render() runs twice a
   *  second: replaceChildren() took every tile out and put it back each time, which drops the tile's :hover (its 固定
   *  button faded out and in under the mouse, the flicker) and pauses its camera video until it plays again. */
  function place(box, els) {
    els.forEach((e, i) => { if (box.children[i] !== e) box.insertBefore(e, box.children[i] || null); });
    while (box.children.length > els.length) box.lastElementChild.remove();
  }
  /** A tile that did move (pinned: grid to stage) was paused by the browser on the way: start its camera again. */
  function replay(ts) { for (const [, t] of ts) if (t.stream && t.video.paused && t.el.isConnected) t.video.play().catch(() => {}); }
  /** Lay out everything for the current state (cheap enough to run twice a second: it moves nothing that stays put). */
  function render() {
    if (!room) return;
    const list = people(), sc = screenOf(list);
    for (const [id, t] of tiles) if (!list.some((x) => x.id === id)) { t.el.remove(); tiles.delete(id); }
    if (pinned && !list.some((x) => x.id === pinned)) pinned = null;
    const ic = room.call ? 'phone' : 'cam', rung = room.call ? o.call?.view()?.rung ?? [] : [], rec = room.call ? null : o.notes?.live(room.id);
    put(title, ic, room.name); count.textContent = t('meeting.count', { n: list.length }); put(miniTitle, ic, t(rec?.paused ? 'meeting.miniPaused' : rec ? 'meeting.miniRec' : 'meeting.mini', { room: room.name, n: list.length }));
    rungEl.textContent = rung.length ? t('meeting.ringing', { names: rung.join(t('meeting.sep')) }) : '';
    if (room.call) rch.replaceChildren(); else o.raftChannel?.(rch, room);
    recTag.classList.toggle('on', !!rec); recTag.classList.toggle('paused', !!rec?.paused);
    recLabel.textContent = t(rec?.paused ? 'meeting.recordingPaused' : 'meeting.recording');
    recTag.title = rec ? t(rec.paused ? 'notes.unavailable' : 'meeting.recBy', { name: rec.by }) : '';
    if (bNotes) bNotes.style.display = o.notes.supported() ? '' : 'none';
    if (bRec) bRec.style.display = o.notes.supported() && (rec || o.notes.canStart()) ? '' : 'none'; // guests: 停止记录 only
    if (bRec) { const w = WORDS.rec[rec ? 'on' : 'off']; bRec.classList.toggle('rec', !!rec); bRec._l.textContent = t(w.text); bRec.title = t(w.tip); }
    bGal.classList.toggle('on', layout === 'gallery' && !pinned && !sc); bSpk.classList.toggle('on', layout === 'speaker' || !!pinned || !!sc);
    const ts = list.map((x) => [x, tileFor(x)]);
    // what takes the stage: a screen share, else the pinned tile, else (speaker view) whoever talks
    let big = null;
    if (!sc) {
      if (pinned) big = pinned;
      else if (layout === 'speaker') big = (list.length > 1 && (active && list.some((x) => x.id === active) ? active : list.find((x) => !x.mine)?.id)) || 'me';
    }
    if (mode === 'mini') {
      const order = [...ts].sort(([a], [b]) => (b.id === active) - (a.id === active)).slice(0, MINI_TILES);
      place(miniStrip, order.map(([, t]) => t.el));
      for (const [, t] of order) t.el.style.width = t.el.style.height = '';
      for (const [, t] of ts) if (!order.some(([, u]) => u === t)) t.el.remove();
      replay(ts);
      return;
    }
    root.classList.toggle('staged', !!(sc || big));
    if (sc) { place(stage, [screenEl(sc)]); place(strip, ts.map(([, t]) => t.el)); place(grid, []); }
    else if (big) {
      const [, bt] = ts.find(([x]) => x.id === big);
      place(stage, [bt.el]); bt.el.style.width = bt.el.style.height = '';
      place(strip, ts.filter(([x]) => x.id !== big).map(([, t]) => t.el)); place(grid, []);
    } else { place(stage, []); place(strip, []); place(grid, ts.map(([, t]) => t.el)); fitGrid(ts.length); }
    if (!sc && screenBox?.firstChild.srcObject) screenBox.firstChild.srcObject = null; // the share ended: let go of it
    for (const c of strip.children) c.style.width = c.style.height = '';
    replay(ts);
    // the toolbar
    const muted = o.mic.muted(), ready = o.mic.ready();
    bMic.classList.toggle('off', muted || !ready); { const ic = muted || !ready ? 'mic_off' : 'mic'; if (bMic._i.firstChild?.dataset.icon !== ic) bMic._i.replaceChildren(icon(ic, { size: 32 })); }
    bMic._l.textContent = t(WORDS.mic[!ready ? 'none' : muted ? 'muted' : 'on']);
    bCam.classList.toggle('off', !cam.on); bCam._l.textContent = cam.busy ? '…' : t(WORDS.cam[cam.on ? 'on' : 'off']);
    bShare.classList.toggle('live', o.share.live()); bShare._l.textContent = t(WORDS.share[o.share.live() ? 'live' : 'off']);
    bCc.classList.toggle('off', !o.cc.on()); bCc._l.textContent = t(WORDS.cc[o.cc.on() ? 'on' : 'off']);
    bCc.disabled = o.cc.available?.() === false; bCc.title = t(bCc.disabled ? 'app.voice.sttUnavailable' : 'meeting.bar.cc.tip');
    bPeople._l.textContent = t('meeting.bar.people', { n: list.length });
    chatBadge.textContent = unread; chatBadge.style.display = unread && side !== 'chat' ? '' : 'none';
    if (side === 'people') renderPeople(list);
  }
  function renderPeople(list) {
    const hs = hostState(), iHost = !!hs && hs.host === meNow()?.id;
    if (menuFor && (!iHost || !list.some((x) => x.id === menuFor))) menuFor = null;
    // the host first, then as before (me first)
    const rows = [...list].sort((a, b) => isHostX(b) - isHostX(a)).map((x) => {
      const p = x.p, muted = x.mine ? o.mic.muted() : !!p.muted;
      const mic = p.kind === 'agent' ? 'chat' : muted ? 'mic_off' : 'mic';
      const icons = [mic, (camStreamOf(x) || (!x.mine && p.camTrack)) && 'cam', (x.mine ? o.share.live() : p.screenTrack) && 'screen'].filter(Boolean);
      return { x, p, muted, icons, host: isHostX(x), menu: iHost && !x.mine, open: menuFor === x.id, name: nameOf(o.agentName(p), x.mine), stt: transcribed(x) };
    });
    const sig = JSON.stringify([rows.map((r) => [r.x.id, r.muted, r.icons, r.host, r.menu, r.open, r.name, r.stt]), t('meetHost.host')]);
    if (sig !== peopleSig) {
      peopleSig = sig;
      peopleList.replaceChildren(...rows.flatMap((w) => {
        const r = el('div', 'row'), out = [r];
        r.append(put(el('span', 'nn'), w.p.kind === 'agent' ? 'robot' : null, w.name));
        if (w.host) r.append(put(el('span', 'hb'), 'crown', t('meetHost.host')));
        if (w.stt) r.append(icon('transcribing', { title: sttTip(w.x) }));
        const ic = el('span', 'ic'); ic.append(...w.icons.map((n) => icon(n))); r.append(ic);
        if (w.menu) {
          const mm = el('button', 'mm' + (w.open ? ' on' : '')); mm.append(icon(w.open ? 'caret_up' : 'caret_down')); mm.title = t('meetHost.menu');
          mm.onclick = () => { menuFor = w.open ? null : w.x.id; render(); };
          r.append(mm);
        }
        if (w.open) {
          const m = el('div', 'rmenu');
          if (w.p.kind !== 'agent') {
            const b = put(el('button'), 'mic_off', t('meetHost.op.mute')); b.disabled = w.muted;
            b.onclick = () => { o.host.mute(w.x.id); menuFor = null; render(); }; m.append(b);
          }
          const go = put(el('button', 'bad'), 'door', t('meetHost.op.remove'));
          go.onclick = () => { o.host.remove(w.x.id); menuFor = null; render(); }; m.append(go);
          out.push(m);
        }
        return out;
      }));
    }
    // the foot: 邀请, 静音我 / 认领主持人, or (the host) 全员静音 / 锁门
    const inRoom = !!room && !room.call && !!o.host, ready = o.mic.ready(), mutedMe = o.mic.muted();
    bInvite.style.display = canInvite() ? '' : 'none';
    bMuteMe.style.display = inRoom && ready ? '' : 'none';
    put(bMuteMe, mutedMe ? 'mic' : 'mic_off', t(mutedMe ? 'meetHost.foot.unmuteMe' : 'meetHost.foot.muteMe'));
    const v = hs?.vote, claimable = inRoom && !iHost && o.host.canClaim();
    bClaim.style.display = claimable ? '' : 'none'; bClaim.disabled = !!v;
    put(bClaim, 'crown', t('meetHost.foot.claim')); bClaim.title = t('meetHost.foot.claimTip');
    bMuteAll.style.display = bLock.style.display = inRoom && iHost ? '' : 'none';
    put(bMuteAll, 'mic_off', t('meetHost.foot.muteAll'));
    bLock.classList.toggle('on', !!hs?.locked); put(bLock, 'door', t(hs?.locked ? 'meetHost.foot.unlock' : 'meetHost.foot.lock'));
    bLock.title = t(hs?.locked ? 'meetHost.foot.unlockTip' : 'meetHost.foot.lockTip');
    const line = !inRoom ? '' : v ? t('meetHost.vote.on', { name: v.name, yes: v.yes, n: v.n }) : hs?.locked ? t('meetHost.lockedNote') : '';
    hostNote.textContent = line; hostNote.style.display = line ? '' : 'none';
  }
  /** The host of the room I'm in, as the office says (public/meethost.js), or null (no room, a call). */
  const hostState = () => (room && !room.call ? o.host?.state(room.id) ?? null : null);
  /** Is this participant the room's host? */
  const isHostX = (x) => { const h = hostState()?.host; return !!h && h === (x.mine ? meNow()?.id : x.id); };
  /** Is there an 邀请 (a 会议室: the members picker) or 添加 (a call) to offer at the foot of the list? */
  const canInvite = () => !!room && !!(room.call ? o.call?.add : o.invite);
  const transcribed = (x) => (x.mine ? o.cc.me() : !!x.p.stt);
  const sttTip = (x) => (x.mine ? o.cc.tip() : o.cc.tipOther);

  // ---- 字幕: one line per speaker, updated in place (the sentence so far, then its final, then the next sentence)
  function caption(m, mine) {
    if (!room || !o.cc.on() || !m.text) return;
    const id = mine ? 'me' : m.id, now = Date.now();
    let c = caps.get(id);
    if (!c) { c = { el: el('div', 'cl'), nm: el('span', 'n'), x: el('span', 'x'), at: 0 }; c.el.append(c.nm, c.x); caps.set(id, c); capBox.append(c.el); }
    const cps = [...m.text];
    c.nm.textContent = saidBy(mine ? t('meeting.you') : m.name);
    c.x.textContent = cps.length > CAP_TAIL ? '…' + cps.slice(-CAP_TAIL).join('') : m.text;
    c.el.classList.toggle('partial', !!m.partial); c.el.classList.toggle('mine', mine); c.el.classList.remove('gone');
    c.at = now;
    // more speakers than CAP_LINES: the one quiet longest goes (lines keep their place while they're updated)
    while (caps.size > CAP_LINES) { const [old] = [...caps].sort(([, a], [, b]) => a.at - b.at)[0]; caps.get(old).el.remove(); caps.delete(old); }
  }
  function capFade(now) {
    for (const [id, c] of caps) {
      if (now - c.at > CAP_FADE_MS + 700) { c.el.remove(); caps.delete(id); }
      else if (now - c.at > CAP_FADE_MS) c.el.classList.add('gone');
    }
  }
  function capClear() { for (const c of caps.values()) c.el.remove(); caps.clear(); }

  /** The right-hand panel: 'chat', 'people' or null. byHand: I pressed something, so remember whether chat is open. */
  function setSide(s, byHand = false, focus = byHand) {
    if (byHand && (s === 'chat' || side === 'chat')) { try { localStorage.setItem(MEET_CHAT_KEY, s === 'chat' ? '1' : '0'); } catch {} }
    side = s; root.classList.toggle('side', !!s);
    put(sideTitle, s === 'chat' ? 'chat' : 'people', t(s === 'chat' ? 'meeting.side.chat' : 'meeting.side.people'));
    chatList.style.display = chatForm.style.display = s === 'chat' ? '' : 'none';
    peopleList.style.display = s === 'people' ? '' : 'none';
    peopleFoot.style.display = s === 'people' && (canInvite() || (!!room && !room.call && !!o.host)) ? '' : 'none';
    peopleSig = '';
    if (s === 'chat') { unread = 0; chatList.scrollTop = 1e9; if (focus) chatIn.focus(); }
    render();
  }
  function setMode(m) {
    mode = m;
    document.body.classList.toggle('meet-full', !!room && m === 'full' && !leaving);
    document.body.classList.toggle('meet-mini', !!room && (m === 'mini' || leaving));
    if (m === 'full') { for (const t of tiles.values()) t.el.remove(); }
    const onMeet = m === 'full' && !leaving;
    for (const v of [vsHead, vsMini]) { v._a.classList.toggle('on', onMeet); v._b.classList.toggle('on', !onMeet); }
    if (room && m === 'full') { o.mover.keys.clear(); o.mover.unpush?.(); }
    render();
  }

  /** One line into the meeting's chat panel (mine: said by this connection; the office's log keeps connection ids). */
  function chatLine(m) {
    const line = el('div', 'line' + (m.id === o.me()?.id ? ' mine' : ''));
    line.append(el('span', 't', fmtTime(m.at || Date.now())), el('span', 'n', saidBy(m.name)), el('span', '', m.text));
    chatList.querySelector('.empty')?.remove();
    chatList.append(line); while (chatList.children.length > CHAT_KEEP) chatList.firstChild.remove();
  }

  // ---- entering and leaving the room
  function enter(z) {
    room = z; shareEpoch++; leaving = false; pinned = null; active = null; unread = 0;
    spokeAt.clear(); document.body.classList.add('meet-in');
    chatList.replaceChildren(tr(el('div', 'empty'), 'meeting.chat.empty'));
    if (!z.call && kept.room === z.id) for (const m of kept.lines) chatLine(m);
    // a call: no room chat or 白板 (its people may be anywhere); 添加 rings more people, 挂断 leaves
    root.classList.toggle('call', !!z.call); mini.classList.toggle('call', !!z.call); document.body.classList.toggle('meet-call', !!z.call);
    bBoard.classList.add('bd'); bChat.classList.add('ch');
    words();
    setSide(!z.call && chatWanted() ? 'chat' : null); setMode('full');
    if (!z.call) o.notes?.entered(z.id); // 正在记录: the notice for whoever walks in
    if (!z.call) o.calendar?.entered(z.id); // 日历: a meeting booked with 会议纪要 offers 开始记录 (public/calendar.js)
  }
  /** Does the room chat open when I walk in? What I chose last; else yes, but not on a narrow screen (it covers the tiles). */
  function chatWanted() {
    let v = null; try { v = localStorage.getItem(MEET_CHAT_KEY); } catch {}
    return v === '1' || (v !== '0' && !matchMedia('(max-width:640px)').matches);
  }
  /** keep: going straight to another view (call ↔ room): the camera stays on (the office drops it if no longer
   *  allowed). Screen sharing stops: the new space needs a fresh choice. */
  function exit(keep = false) {
    room = null; shareEpoch++; leaving = false; root.classList.remove('call'); mini.classList.remove('call'); document.body.classList.remove('meet-call');
    for (const t of tiles.values()) t.el.remove(); tiles.clear(); capClear();
    stage.replaceChildren(); strip.replaceChildren(); grid.replaceChildren(); miniStrip.replaceChildren();
    if (screenBox) screenBox.firstChild.srcObject = null;
    document.body.classList.remove('meet-full', 'meet-mini', 'meet-in');
    o.share.stop(); // also cancels a picker or a publish in progress, immediately on leaving this space
    if (keep) return;
    if (cam.on || cam.stream) o.rtc.serial(camStop);
  }
  /** 离开会议室: walk out of the door (the nearest deck tile two steps outside the room), the map in view meanwhile. */
  async function leave() {
    if (room?.call) { o.call.hangup(); return; } // in a call: 挂断
    const me = meNow(); if (!me || !room) return;
    const z = room, inside = (x, y) => x >= z.x && x < z.x + z.w && y >= z.y && y < z.y + z.h;
    const away = (x, y) => Math.max(z.x - x, x - (z.x + z.w - 1), z.y - y, y - (z.y + z.h - 1));
    const sx = Math.floor(me.x + 0.5), sy = Math.floor(me.y + 0.8), seen = new Set([sx + ',' + sy]), q = [[sx, sy]];
    let to = null;
    while (q.length) {
      const [x, y] = q.shift();
      if (!inside(x, y) && away(x, y) >= 2) { to = [x, y]; break; }
      for (const [dx, dy] of [[0, 1], [1, 0], [-1, 0], [0, -1]]) {
        const nx = x + dx, ny = y + dy, k = nx + ',' + ny;
        if (!seen.has(k) && o.mover.footOk(nx, ny)) { seen.add(k); q.push([nx, ny]); }
      }
      if (seen.size > 4000) break;
    }
    if (cam.on) o.rtc.serial(camStop);
    if (!to) { o.toast(t('meeting.leave.noDoor')); setMode('mini'); return; }
    const started = await o.mover.travelTo(to[0], to[1], true);
    if (started === null || room !== z) return;
    if (!started) { o.toast(t('meeting.leave.noDoor')); setMode('mini'); return; }
    leaving = true; o.share.stop(); setMode(mode);
  }

  // ---- my camera: a third track on the SFU session (the mic's), announced to the office as "cam"
  /** 📷: the permission prompt (getUserMedia) runs outside the SFU queue, so voice pulls never wait behind it; the
   *  publish itself is queued like every other SFU step. */
  async function toggleCam() {
    if (cam.busy) return;
    if (cam.on) { o.rtc.serial(camStop); return; }
    if (!room) return;
    if (o.rtc.videoAvailable?.() === false) { o.toast(t('app.voice.videoUnavailable')); return; }
    if (!o.rtc.ready()) { o.toast(t('meeting.cam.noVoice')); return; }
    cam.busy = true; render();
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ video: CAM_CONSTRAINTS, audio: false }); }
    catch (e) {
      cam.busy = false; render();
      o.toast(t(e?.name === 'NotAllowedError' || e?.name === 'SecurityError' ? 'meeting.cam.denied'
        : e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError' ? 'meeting.cam.notFound' : e?.name === 'NotReadableError' ? 'meeting.cam.busy' : 'meeting.cam.failed'));
      return;
    }
    o.rtc.serial(() => camPublish(stream));
  }
  /** Camera permission and UI stay here; the selected transport publishes the captured track. */
  function camPublish(stream) {
    return o.rtc.publishCam(stream, { cam, inRoom: () => !!room, maxBitrate: CAM_MAX_BITRATE,
      stop: camStop, onError: () => o.toast(t('meeting.cam.retry')), render });
  }
  async function camStop() {
    const { mid, stream, on, transceiver } = cam;
    Object.assign(cam, { on: false, mid: null, stream: null, name: null, transceiver: null });
    stream?.getTracks().forEach((t) => t.stop());
    if (on) o.send({ t: 'cam', trackName: null });
    render();
    await o.rtc.unpublishCam(mid, transceiver);
  }

  // ---- every frame-ish: am I in a meeting room, who is talking
  function tick() {
    const me = meNow();
    const z = me ? o.zoneAt(me.x, me.y) : null, mz = z?.meeting ? z : null;
    // in a call, the view shows the call (wherever I stand); else the meeting room I'm in
    // (only once someone else is in it: while I'm still ringing people, a 会议室 I'm in stays as it is)
    const cv = me ? o.call?.view() : null, callOn = !!cv && cv.list.length > 0;
    const want = callOn ? (room?.call && room.cid === cv.id ? room : { id: 'call:' + cv.id, cid: cv.id, name: t('meeting.call'), call: true }) : mz;
    // from one view straight to the other: camera may stay on; screen sharing stops at the boundary
    if (want?.id !== room?.id) { if (room) exit(!!want); if (want) enter(want); }
    if (!room) return;
    const list = people(), now = Date.now();
    capFade(now);
    let cur = null; // who talks now (not me): the current speaker if they still do, else the first one who does
    for (const x of list) {
      const talking = (x.p.kind !== 'agent' || !!x.p.clip) && o.speaking(x.p); // agents talk only in clips (deck say --voice)
      tiles.get(x.id)?.el.classList.toggle('speaking', talking);
      if (talking && !x.mine) spokeAt.set(x.id, now);
      if (talking && !x.mine && (!cur || x.id === active)) cur = x.id;
    }
    if (cur && cur === active) activeAt = now;
    else if (cur && (!active || now - activeAt > SPEAK_HOLD_MS)) { active = cur; activeAt = now; if (layout === 'speaker' && !pinned) render(); }
  }
  setInterval(tick, 120);
  setInterval(render, 500);
  addEventListener('resize', () => render());

  return {
    /** The meeting covers the map: the map's walking keys wait (M, Space, chat still work). */
    full: () => !!room && mode === 'full' && !leaving,
    /** Enter on the map while the meeting is up: the meeting's chat. */
    openChat() { setSide('chat', true); },
    /** A chat line arrived (app.js chatGot): in the room it shows in the meeting's chat too. */
    chat(m, mine) {
      if (!room || room.call) return;
      if (kept.room !== room.id) kept = { room: room.id, lines: [] };
      kept.lines.push(m); if (kept.lines.length > CHAT_KEEP) kept.lines.shift();
      const stick = chatList.scrollTop + chatList.clientHeight >= chatList.scrollHeight - 8;
      chatLine(m);
      if (stick || mine) chatList.scrollTop = 1e9;
      if (side !== 'chat' && !mine) unread++;
      render();
    },
    /** {t:"chat_log", room, lines}: the meeting's chat so far, sent as I walk into the room (or connect in it): it
     *  replaces what the panel shows (an empty list: no meeting on there that I'm part of). */
    chatLog(m) {
      if (typeof m.room !== 'string' || !Array.isArray(m.lines)) return;
      kept = { room: m.room, lines: m.lines.slice(-CHAT_KEEP) };
      if (!room || room.call || room.id !== m.room) return; // (enter() shows it when the page gets there)
      chatList.replaceChildren(tr(el('div', 'empty'), 'meeting.chat.empty'));
      for (const x of kept.lines) chatLine(x);
      chatList.scrollTop = 1e9; render();
    },
    /** A caption ({t:"caption"}, or my own words so far from stt.js with mine = true): into the strip, if 字幕 is on. */
    caption,
    /** The office refused or dropped my camera ("cam" null for me): stop sending it. */
    camDropped() { if (cam.on) o.rtc.serial(camStop); },
    /** Someone's tracks changed (a camera or a screen arrived or went): redraw now. */
    refresh: () => render(),
    /** The page stops (another tab took over): the camera goes off. */
    stop() { cam.stream?.getTracks().forEach((t) => t.stop()); cam.on = false; cam.stream = null; exit(); },
    /** A (re)connected office was just welcomed: it knows nothing of my camera, so tell it again (it refuses if I'm
     *  no longer in a meeting room, and camDropped cleans up). */
    welcome() { if (cam.on && cam.name && room && !room.call) o.send({ t: 'cam', trackName: cam.name }); },
    /** My call changed (or came back after a reconnect): tell the office my camera again (it ignores a repeat). */
    recam() { if (cam.on && cam.name && room?.call) o.send({ t: 'cam', trackName: cam.name }); },
    /** In a call (its view is up, or the small window)? */
    inCall: () => !!room?.call,
    /** A screen-share authorization for the current visit to this exact room/call, or null while leaving. Check the
     *  live position/call as well as this view: its 120 ms redraw can otherwise lag an async picker or publish. */
    shareScope() {
      const me = meNow(); if (!room || leaving || !me) return null;
      const call = o.call?.view();
      if (room.call) { if (!call || call.id !== room.cid || !call.list.length) return null; }
      else { const z = o.zoneAt(me.x, me.y); if (call?.list.length || !z?.meeting || z.id !== room.id) return null; }
      return shareEpoch;
    },
    /** The toolbar's 通话 while in one: the call view, full. */
    openFull() { if (room) { leaving = false; setMode('full'); } },
    /** app.js pulls someone's camera only if this says so: in my room, and among the CAM_PULL_MAX people with a
     *  camera who talked last (stable order otherwise), or pinned. */
    camAllowed(id) {
      if (!room) return false;
      if (id === pinned) return true;
      const ids = room.call ? people().filter((x) => !x.mine && x.p.camTrack).map((x) => x.id)
        : [...o.peers()].filter(([, p]) => p.camTrack && o.zoneAt(p.x, p.y)?.id === room.id).map(([pid]) => pid);
      if (ids.length <= CAM_PULL_MAX) return ids.includes(id);
      ids.sort((a, b) => (spokeAt.get(b) || 0) - (spokeAt.get(a) || 0));
      return ids.slice(0, CAM_PULL_MAX).includes(id);
    },
    /** For app.js's subscription rule: the meeting room I'm in (zone id), or null (also in a call: inCall). */
    room: () => (room && !room.call ? room.id : null),
    /** For the 小窗 (public/pip.js): the room, who's in it ({id, p, mine, talking, cam: MediaStream|null}), the active
     *  speaker's id ('me' never: it's who talks besides me), my camera, and its controls. null outside a 会议室. */
    info() {
      if (!room) return null;
      const list = people().map((x) => ({ ...x, talking: !!tiles.get(x.id)?.el.classList.contains('speaking'), cam: camStreamOf(x) }));
      return { id: room.id, name: room.name, call: !!room.call, list, active, cam: { on: cam.on, busy: cam.busy } };
    },
    toggleCam: () => toggleCam(),
    /** The meeting view again (public/rooms.js 回到会议), if I'm in a 会议室. */
    show: () => { if (room && !leaving) setMode('full'); },
    /** Would show() open it now (in a 会议室, not on my way out, not open already)? */
    canShow: () => !!room && !leaving && mode !== 'full',
    leave: () => leave(),
  };
}
